/**
 * File: contextManager.ts
 * Description: Global context manager for tracking user state and interactions
 * Author: System
 * Created: 2025-08-27
 * Last Modified: 2025-08-27
 */

import {
  ContextData,
  KeyValue,
  ContextChangeEvent,
  IContextManager,
} from '../types/context';
import { logger } from '../utils/logger';

/**
 * Singleton context manager for global state tracking
 */
class ContextManager implements IContextManager {
  private static instance: ContextManager;
  private context: ContextData = {};
  private subscribers: Array<(context: KeyValue[]) => void> = [];
  private changeHistory: ContextChangeEvent[] = [];
  private readonly maxHistorySize = 100;

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance of context manager
   */
  public static getInstance(): ContextManager {
    if (!ContextManager.instance) {
      ContextManager.instance = new ContextManager();
    }
    return ContextManager.instance;
  }

  /**
   * Get current context as key-value pairs for API
   */
  public getContext(): KeyValue[] {
    return Object.entries(this.context)
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => ({ key, value: value! }));
  }

  /**
   * Get raw context data object
   */
  public getContextData(): ContextData {
    return { ...this.context };
  }

  /**
   * Update context with new key-value pair
   */
  public updateContext(
    key: string, 
    value: string, 
    source: string = 'unknown',
  ): void {
    const previousValue = this.context[key];
    
    if (previousValue === value) {
      return; // No change, skip update
    }

    this.context[key] = value;

    // Record change event
    const changeEvent: ContextChangeEvent = {
      key,
      value,
      timestamp: new Date(),
      source,
    };

    this.changeHistory.push(changeEvent);

    // Maintain history size limit
    if (this.changeHistory.length > this.maxHistorySize) {
      this.changeHistory.shift();
    }

    // Notify subscribers
    this.notifySubscribers();
  }

  /**
   * Remove context key
   */
  public removeContext(key: string): void {
    if (key in this.context) {
      delete this.context[key];
      this.notifySubscribers();
    }
  }

  /**
   * Clear all context data
   */
  public clearContext(): void {
    this.context = {};
    this.changeHistory = [];
    this.notifySubscribers();
  }

  /**
   * Subscribe to context changes
   */
  public subscribe(callback: (context: KeyValue[]) => void): () => void {
    this.subscribers.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  /**
   * Get recent context changes for debugging
   */
  public getChangeHistory(): ContextChangeEvent[] {
    return [...this.changeHistory];
  }

  /**
   * Update multiple context values at once
   */
  public updateMultipleContext(
    updates: Record<string, string>,
    source: string = 'bulk_update',
  ): void {
    let hasChanges = false;

    Object.entries(updates).forEach(([key, value]) => {
      if (this.context[key] !== value) {
        this.context[key] = value;
        hasChanges = true;

        // Record change event
        this.changeHistory.push({
          key,
          value,
          timestamp: new Date(),
          source,
        });
      }
    });

    if (hasChanges) {
      // Maintain history size limit
      if (this.changeHistory.length > this.maxHistorySize) {
        const excess = this.changeHistory.length - this.maxHistorySize;
        this.changeHistory.splice(0, excess);
      }

      this.notifySubscribers();
    }
  }

  /**
   * Initialize context with project-specific data
   * @param projectId - Project ID for context initialization
   * @param initialData - Initial context data
   */
  public initializeWithProject(
    projectId: number,
    initialData: Record<string, string> = {},
  ): void {
    // Clear existing context
    this.clearContext();
    
    // Set project ID
    this.updateContext('project_id', projectId.toString(), 'project_init');
    
    // Set initial data
    Object.entries(initialData).forEach(([key, value]) => {
      this.updateContext(key, value, 'project_init');
    });
  }

  /**
   * Get context summary for debugging
   */
  public getContextSummary(): {
    totalKeys: number;
    lastUpdated: Date | null;
    recentChanges: number;
    } {
    const contextKeys = this.getContext();
    const lastChange = this.changeHistory[this.changeHistory.length - 1];
    const recentChanges = this.changeHistory.filter(
      change => Date.now() - change.timestamp.getTime() < 60000, // Last minute
    ).length;

    return {
      totalKeys: contextKeys.length,
      lastUpdated: lastChange?.timestamp || null,
      recentChanges,
    };
  }

  /**
   * Notify all subscribers of context changes
   */
  private notifySubscribers(): void {
    const context = this.getContext();
    this.subscribers.forEach(callback => {
      try {
        callback(context);
      } catch (error) {
        logger.error('Error in context subscriber:', error);
      }
    });
  }
}

// Export singleton instance
export const contextManager = ContextManager.getInstance();
export default contextManager;
